import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { authMe, authLogout } from "../lib/api";
import { chatSocket } from "../lib/chatSocket";

const UserContext = createContext(null);

/** user === undefined → still checking. null → signed out. object → signed in. */
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(undefined);
  const [chatOpen, setChatOpen] = useState(false);
  const [activeConversation, setActiveConversation] = useState(null);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [wsEvent, setWsEvent] = useState(null);
  const wsBoundRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const u = await authMe();
      setUser(u);
      return u;
    } catch (_) {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Open the chat WebSocket as soon as we know we're authed.
  useEffect(() => {
    if (!user || wsBoundRef.current) return;
    wsBoundRef.current = true;
    chatSocket.connect();
    const off = chatSocket.on((evt) => {
      // Always surface the latest event so subscribers (ChatPanel) can react.
      setWsEvent({ ...evt, _at: Date.now() });
      if (evt?.type === "unread_count" && typeof evt.total === "number") {
        setUnreadTotal(evt.total);
      }
    });
    return () => {
      off();
      chatSocket.close();
      wsBoundRef.current = false;
    };
  }, [user?.id]);

  const signOut = useCallback(async () => {
    try { await authLogout(); } catch (_) {}
    chatSocket.close();
    wsBoundRef.current = false;
    setUser(null);
    setUnreadTotal(0);
  }, []);

  const openChat = (conversation = null) => {
    setActiveConversation(conversation);
    setChatOpen(true);
  };
  const closeChat = () => setChatOpen(false);

  return (
    <UserContext.Provider value={{
      user, setUser, refresh, signOut,
      chatOpen, setChatOpen, activeConversation, setActiveConversation, openChat, closeChat,
      unreadTotal, wsEvent, chatSocket,
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
